import { z } from 'zod';
import { platformOptionalFactUnavailableReasonSchema } from '@crest/platform-contracts/hosts';
import { insuranceClaimsClaimSchema, insuranceClaimsIdSchema, insuranceClaimsMoneyCentsSchema, } from './insurance-claims-claim.contract.js';
import { insuranceClaimsSupplementSummarySchema } from './insurance-claims-supplement.contract.js';
export const insuranceClaimsReadinessCapability = 'insurance-claims.readiness.v1';
export const insuranceClaimsProjectSummariesCapability = 'insurance-claims.project-summaries.v1';
export const insuranceClaimsReadinessFactStateSchema = z.enum([
    'present', 'missing', 'unavailable',
]);
const sourceCapabilitySchema = z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+\.v[1-9][0-9]*$/);
export const insuranceClaimsReadinessFactSchema = z.discriminatedUnion('state', [
    z.strictObject({
        state: z.literal('present'),
        count: z.number().int().positive().safe(),
        sourceCapability: sourceCapabilitySchema,
        reasonCode: z.null(),
    }),
    z.strictObject({
        state: z.literal('missing'),
        count: z.literal(0),
        sourceCapability: sourceCapabilitySchema,
        reasonCode: z.null(),
    }),
    z.strictObject({
        state: z.literal('unavailable'),
        count: z.null(),
        sourceCapability: sourceCapabilitySchema,
        reasonCode: platformOptionalFactUnavailableReasonSchema,
    }),
]);
export const insuranceClaimsReadinessFactsSchema = z.strictObject({
    workAuthorization: insuranceClaimsReadinessFactSchema,
    roomsDimensions: insuranceClaimsReadinessFactSchema,
    scope: insuranceClaimsReadinessFactSchema,
    photos: insuranceClaimsReadinessFactSchema,
    scanEvidence: insuranceClaimsReadinessFactSchema,
    psychrometrics: insuranceClaimsReadinessFactSchema,
    dailyLogs: insuranceClaimsReadinessFactSchema,
    dryGoal: insuranceClaimsReadinessFactSchema,
    equipment: insuranceClaimsReadinessFactSchema,
    antimicrobial: insuranceClaimsReadinessFactSchema,
    estimate: insuranceClaimsReadinessFactSchema,
    contents: insuranceClaimsReadinessFactSchema,
    certificateOfCompletion: insuranceClaimsReadinessFactSchema,
    moldClearance: insuranceClaimsReadinessFactSchema,
    time: insuranceClaimsReadinessFactSchema,
});
export const insuranceClaimsReadinessInputSchema = z.strictObject({
    claim: insuranceClaimsClaimSchema,
    facts: insuranceClaimsReadinessFactsSchema,
});
export const insuranceClaimsReadinessItemKeySchema = z.enum([
    'claim_data',
    'work_auth',
    'cause',
    'water_category',
    'water_class',
    'rooms_dims',
    'scope',
    'photos',
    'psychrometrics',
    'daily_logs',
    'dry_goal',
    'equipment',
    'antimicrobial',
    'estimate',
    'contents',
    'coc',
    'mold_clearance',
    'time',
]);
export const insuranceClaimsReadinessDefenseGroupSchema = z.enum([
    'covered', 'necessary', 'done-effective',
]);
export const insuranceClaimsReadinessItemSchema = z.strictObject({
    key: insuranceClaimsReadinessItemKeySchema,
    label: z.string().trim().min(1).max(240),
    defenseGroup: insuranceClaimsReadinessDefenseGroupSchema,
    required: z.boolean(),
    done: z.boolean(),
    state: z.enum(['present', 'missing', 'unavailable', 'not-required']),
    sourceCapability: z.string().nullable(),
    reasonCode: platformOptionalFactUnavailableReasonSchema.nullable(),
});
export const insuranceClaimsReadinessSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsReadinessCapability),
    claimId: insuranceClaimsIdSchema,
    projectId: insuranceClaimsIdSchema,
    status: z.enum(['complete', 'incomplete', 'partial']),
    percent: z.number().int().min(0).max(100).nullable(),
    ready: z.boolean(),
    doneCount: z.number().int().nonnegative(),
    requiredCount: z.number().int().nonnegative(),
    unavailableCount: z.number().int().nonnegative(),
    nextMissing: z.strictObject({
        key: insuranceClaimsReadinessItemKeySchema,
        label: z.string().trim().min(1).max(240),
    }).nullable(),
    missing: z.array(z.strictObject({
        key: insuranceClaimsReadinessItemKeySchema,
        label: z.string().trim().min(1).max(240),
    })),
    unavailable: z.array(z.strictObject({
        key: insuranceClaimsReadinessItemKeySchema,
        label: z.string().trim().min(1).max(240),
        sourceCapability: z.string(),
        reasonCode: platformOptionalFactUnavailableReasonSchema,
    })),
    items: z.array(insuranceClaimsReadinessItemSchema),
    context: z.strictObject({
        isWater: z.boolean(),
        isMold: z.boolean(),
        waterCategory: z.number().int().min(1).max(3).nullable(),
        requiresAntimicrobial: z.boolean(),
    }),
});
export const insuranceClaimsProjectSummarySchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsProjectSummariesCapability),
    projectId: insuranceClaimsIdSchema,
    claim: insuranceClaimsClaimSchema.nullable(),
    readiness: insuranceClaimsReadinessSchema.nullable(),
    supplements: insuranceClaimsSupplementSummarySchema.nullable(),
});
export const insuranceClaimsProjectSummaryResponseSchema = z.strictObject({
    data: insuranceClaimsProjectSummarySchema,
});
function itemFromFact(input) {
    return {
        key: input.key,
        label: input.label,
        defenseGroup: input.defenseGroup,
        required: input.required,
        done: input.fact.state === 'present',
        state: input.required ? input.fact.state : 'not-required',
        sourceCapability: input.fact.sourceCapability,
        reasonCode: input.required ? input.fact.reasonCode : null,
    };
}
function internalItem(input) {
    return {
        ...input,
        state: input.required ? (input.done ? 'present' : 'missing') : 'not-required',
        sourceCapability: null,
        reasonCode: null,
    };
}
export function computeInsuranceClaimsReadiness(raw) {
    const { claim, facts } = insuranceClaimsReadinessInputSchema.parse(raw);
    const data = claim.currentVersion.data;
    const isWater = data.isWaterLoss;
    const isMold = data.perilType === 'mold' || data.moldCondition !== null;
    const requiresAntimicrobial = isMold || (data.waterCategory !== null && data.waterCategory >= 2);
    const items = [
        internalItem({
            key: 'claim_data',
            label: 'Claim # / carrier / adjuster / deductible',
            defenseGroup: 'covered',
            required: true,
            done: data.claimNumber !== null && data.carrier !== null && data.adjuster !== null,
        }),
        itemFromFact({ key: 'work_auth', label: 'Signed work authorization / AOB', defenseGroup: 'covered', required: true, fact: facts.workAuthorization }),
        internalItem({ key: 'cause', label: 'Cause of loss + date of loss', defenseGroup: 'necessary', required: true, done: data.causeOfLoss !== null && data.lossDate !== null }),
        internalItem({ key: 'water_category', label: 'Water Category (1–3)', defenseGroup: 'necessary', required: isWater, done: data.waterCategory !== null }),
        internalItem({ key: 'water_class', label: 'Water Class (1–4) / mold Condition', defenseGroup: 'necessary', required: isWater || isMold, done: data.waterClass !== null || data.moldCondition !== null }),
        itemFromFact({ key: 'rooms_dims', label: 'Affected rooms + dimensions', defenseGroup: 'necessary', required: true, fact: facts.roomsDimensions }),
        itemFromFact({ key: 'scope', label: 'Scope of work', defenseGroup: 'necessary', required: true, fact: facts.scope }),
        itemFromFact({ key: 'photos', label: 'Photos (before / during / after)', defenseGroup: 'done-effective', required: true, fact: facts.photos }),
        itemFromFact({ key: 'psychrometrics', label: 'Psychrometric readings', defenseGroup: 'done-effective', required: isWater, fact: facts.psychrometrics }),
        itemFromFact({ key: 'daily_logs', label: 'Daily drying/monitoring logs', defenseGroup: 'done-effective', required: isWater, fact: facts.dailyLogs }),
        itemFromFact({ key: 'dry_goal', label: 'Dry standard / drying goal set', defenseGroup: 'done-effective', required: isWater, fact: facts.dryGoal }),
        itemFromFact({ key: 'equipment', label: 'Equipment log (run-days)', defenseGroup: 'done-effective', required: isWater, fact: facts.equipment }),
        itemFromFact({ key: 'antimicrobial', label: 'Antimicrobial / containment record', defenseGroup: 'done-effective', required: requiresAntimicrobial, fact: facts.antimicrobial }),
        itemFromFact({ key: 'estimate', label: 'Line-item estimate', defenseGroup: 'covered', required: true, fact: facts.estimate }),
        itemFromFact({ key: 'contents', label: 'Contents inventory', defenseGroup: 'done-effective', required: false, fact: facts.contents }),
        itemFromFact({ key: 'coc', label: 'Certificate of completion', defenseGroup: 'covered', required: true, fact: facts.certificateOfCompletion }),
        itemFromFact({ key: 'mold_clearance', label: 'Mold PRE/PRV (return to Condition 1)', defenseGroup: 'done-effective', required: isMold, fact: facts.moldClearance }),
        itemFromFact({ key: 'time', label: 'Time / labor records', defenseGroup: 'done-effective', required: false, fact: facts.time }),
    ];
    const required = items.filter((item) => item.required);
    const done = required.filter((item) => item.done);
    const unavailable = required.filter((item) => item.state === 'unavailable');
    const missing = required.filter((item) => item.state === 'missing');
    const percent = unavailable.length > 0
        ? null
        : Math.round((done.length / required.length) * 100);
    const status = unavailable.length > 0
        ? 'partial'
        : missing.length === 0
            ? 'complete'
            : 'incomplete';
    return insuranceClaimsReadinessSchema.parse({
        contractVersion: insuranceClaimsReadinessCapability,
        claimId: claim.id,
        projectId: data.projectReference.projectId,
        status,
        percent,
        ready: status === 'complete',
        doneCount: done.length,
        requiredCount: required.length,
        unavailableCount: unavailable.length,
        nextMissing: missing[0] === undefined
            ? null
            : { key: missing[0].key, label: missing[0].label },
        missing: missing.map(({ key, label }) => ({ key, label })),
        unavailable: unavailable.map(({ key, label, sourceCapability, reasonCode }) => ({
            key,
            label,
            sourceCapability: sourceCapability ?? 'insurance-claims.unknown-provider.v1',
            reasonCode: reasonCode ?? 'provider-unavailable',
        })),
        items,
        context: {
            isWater,
            isMold,
            waterCategory: data.waterCategory,
            requiresAntimicrobial,
        },
    });
}
export function unavailableInsuranceClaimsReadinessFacts(reasonCode = 'not-installed') {
    const unavailable = (sourceCapability) => ({
        state: 'unavailable',
        count: null,
        sourceCapability,
        reasonCode,
    });
    return {
        workAuthorization: unavailable('documents.signed-authorizations.v1'),
        roomsDimensions: unavailable('mitigation.rooms.v1'),
        scope: unavailable('insurance-claims.scopes.v1'),
        photos: unavailable('projects.project-files.v1'),
        scanEvidence: unavailable('scanning-media.scan-captures.v1'),
        psychrometrics: unavailable('mitigation.psychrometrics.v1'),
        dailyLogs: unavailable('mitigation.daily-monitoring.v1'),
        dryGoal: unavailable('mitigation.drying-workspace.v1'),
        equipment: unavailable('assets.placements.v1'),
        antimicrobial: unavailable('mitigation.treatment-records.v1'),
        estimate: unavailable('estimating.project-estimate-summaries.v1'),
        contents: unavailable('mitigation.contents-inventory.v1'),
        certificateOfCompletion: unavailable('documents.signed-completions.v1'),
        moldClearance: unavailable('mitigation.mold-clearance.v1'),
        time: unavailable('timesheets.project-labor-summary.v1'),
    };
}
//# sourceMappingURL=insurance-claims-readiness.contract.js.map